import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import { pesquisarAtalhoAction } from '../../store/Processos/PesquisaUnificada/pesquisaProcessosUnificada.saga';

/**
 * Dispara uma pesquisa "atalho" (`pesquisarAtalhoAction`, AW2-913 — plano de
 * migração de navegação) e navega para `/prototipo/processos` assim que o
 * resultado chegar. Reutilizável por qualquer ponto do protótipo que precise
 * disparar uma busca pré-filtrada sem passar pelo formulário completo de
 * `PesquisaProcessosPage.jsx` (ex.: cards "Minhas Informações", ícones do
 * Topbar).
 *
 * `state.pesquisaProcessosUnificada` é uma slice Redux global — com múltiplas
 * instâncias deste hook montadas ao mesmo tempo (ex.: `Topbar.jsx` e os cards
 * de `PesquisaProcessosPage.jsx`, ambos dentro do mesmo `AppShell`), todas
 * leem o mesmo `resultado`/`erro`. Sem rastrear localmente "fui eu quem
 * disparou", um clique num card faria a instância do `Topbar` também navegar
 * e mostrar seu próprio Snackbar de erro para uma busca que não foi dela.
 * `disparadoRef` resolve isso: só fica `true` durante o ciclo de vida do
 * `disparar()` desta instância específica, e os efeitos abaixo ignoram
 * qualquer mudança de `resultado`/`erro` enquanto ele for `false` — o que já
 * cobre o caso de montagem inicial com dado antigo na store (mesmo problema
 * que a guarda de "ignorar 1ª renderização" de `PesquisaProcessosPage.jsx`
 * resolve, aqui coberto sem precisar de uma guarda separada).
 *
 * **Limitação aceita, não corrigida** (mesma categoria já documentada em
 * `src/prototipo/CLAUDE.md` para saga em voo após unmount): `PESQUISAR_PROCESSOS_ATALHO`
 * usa um único `takeLatest` global (`pesquisaProcessosUnificada.saga.js`) — se
 * a instância A dispara uma busca e, antes da resposta chegar, a instância B
 * dispara outra, `takeLatest` cancela a task de A sem executar
 * `setResultadoAction`/`setErroAction` para ela, então `A.disparadoRef.current`
 * fica preso em `true`. Quando a resposta de B chega, o efeito de A também
 * dispara (navegação duplicada, inofensiva; ou o Snackbar de A mostra o erro
 * de uma busca que não foi dela). Exige 2 cliques em pontos de atalho
 * diferentes dentro da janela de resposta de um GET — corrigir de verdade
 * exigiria um token de correlação por disparo propagado até o reducer,
 * considerado fora de escopo para um módulo de avaliação.
 *
 * @returns {{disparar: (tipo: string, filtroExtra?: object) => void, snackbar: JSX.Element}}
 *   `snackbar` deve ser renderizado pelo consumidor (`{snackbar}`) — mostra o
 *   erro da pesquisa atalho, já que consumidores como `Topbar.jsx` não têm
 *   nenhuma UI de erro própria hoje.
 */
export default function useDispararPesquisaAtalho() {
  const history = useHistory();
  const dispatch = useDispatch();
  const resultado = useSelector(state => state.pesquisaProcessosUnificada.resultado);
  const erro = useSelector(state => state.pesquisaProcessosUnificada.erro);
  const [erroVisivel, setErroVisivel] = useState(null);

  const disparadoRef = useRef(false);

  useEffect(() => {
    if (resultado && disparadoRef.current) {
      disparadoRef.current = false;
      history.push('/prototipo/processos');
    }
  }, [resultado, history]);

  useEffect(() => {
    if (erro && disparadoRef.current) {
      disparadoRef.current = false;
      setErroVisivel(erro);
    }
  }, [erro]);

  const disparar = useCallback((tipo, filtroExtra) => {
    disparadoRef.current = true;
    dispatch(pesquisarAtalhoAction(tipo, filtroExtra));
  }, [dispatch]);

  const snackbar = (
    <Snackbar
      open={Boolean(erroVisivel)}
      autoHideDuration={2200}
      onClose={() => setErroVisivel(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert severity="error" variant="filled" onClose={() => setErroVisivel(null)}>
        {erroVisivel}
      </Alert>
    </Snackbar>
  );

  return { disparar, snackbar };
}
